/* eslint-disable security/detect-object-injection */
import LoadingComponent from '@/components/LoadingComponent/LoadingComponent'
import { useAuth } from '@/providers/auth.provider'
import { IInputProperty } from '@/screens/Order/interface/common.interface'
import {
  getUrlParams,
  isEmptyValue,
  lastDisplayOrder,
  padNumber,
} from '@/utils/common'
import { PlusOutlined } from '@ant-design/icons'
import { Button, Form, FormInstance } from 'antd'
import {
  camelCase,
  find,
  includes,
  keyBy,
  keys,
  map,
  orderBy,
  size,
  sortBy,
} from 'lodash'
import { useEffect, useState } from 'react'
import { getSupplier, verifyPropertyExist } from '../../../Order.transform'
import AddProperty from '../AddProperty/AddProperty'
import EditableFormItem from '../EditableFormItem/EditableFormItem'
import RequiredFields from '../RequiredFields/RequiredFields'
import './OrderDetailForm.style.less'
import {
  IsExternalUser,
  currentUserAssignment,
  disableFields,
} from '../../EditOrder.helper'
import moment from 'moment'
import { useQuery } from 'react-query'
import Fade from '@/components/Fade'
import { getDateObject } from '@/utils/date'
import { IOrderSequentialProperty } from '@/interface/order.interface'
import { getValueDynamic } from '../../../Landing/components/OrderTable/OrderTable.transform'
import { useAtom } from 'jotai'
import { isFieldChangeAtom } from '@/jotai/order.jotai'

interface Props {
  fields: any
  addOnProperties: (fields) => void
  form: FormInstance
  onImport: () => void
  isImport: boolean
  orderData: any
  mode?: 'create' | 'edit' | 'clone'
  orderSetting?: any
  hideProperties?: any
  extraSource?: {
    sequentialItems?: Array<IOrderSequentialProperty>
    sequentialItemsRefetch?: boolean
    settingAutoGenerateData?: any
  }
}

function OrderDetailForm(props: Props) {
  const {
    extraSource: {
      sequentialItemsRefetch,
      sequentialItems = [],
      settingAutoGenerateData = {},
    },
    mode = 'create',
  } = props

  const defaultFieldValue = orderBy(
    props?.orderSetting?.default_fields,
    ['display_order'],
    ['asc']
  )
  const keyDefaultValue: any = keyBy(defaultFieldValue, 'field')
  const stateAllProperties = orderBy(
    props?.fields,
    (item) =>
      item.display_order ??
      keyDefaultValue[item.field]?.display_order ?? ['asc']
  )

  const [, setAtomFieldChange] = useAtom(isFieldChangeAtom)
  const placeHolderField = settingAutoGenerateData?.place_holder_field || {}
  const actionField = settingAutoGenerateData?.action_field || {}
  const itemAutoGenerate = settingAutoGenerateData?.items || []

  const hideProperties = props.hideProperties?.[0] ?? {}
  const [openAddProperty, setOpenAddProperty] = useState(false)

  const [stateSelectProperty, setStateSelectProperty] = useState(null)
  const [loadingAddPropertyValue, setLoadingAddPropertyValue] = useState(false)
  const { user } = useAuth()
  const [errorExistField, setErrorExistField] = useState(false)
  const typeKey = getUrlParams('type')

  const { data: supplier, isLoading: IsLoadinSupplier } = useQuery(
    'GET_SUPPLIER',
    async () => {
      return await getSupplier(props.orderData?.company.id)
    }
  )

  const handleRenderSetFormItem = (dataFields: any) => {
    if (!isEmptyValue(dataFields)) {
      for (const item of dataFields) {
        if (item.data_type == 'date' || item.data_type == 'datetime') {
          if (!isEmptyValue(item.value)) {
            const isValidDate = moment(item.value).isValid()
            const parseDate = isValidDate ? moment(item.value) : null
            props.form.setFieldValue(item.field, parseDate)
          }
        } else if (item.data_type == 'file' || item.data_type == 'image') {
          props.form.setFieldValue(item.field, item.value)
        } else {
          props.form.setFieldValue(item.field, item.value || null)
        }
      }
    }
  }

  // stateAllProperties is using to render custom fields in form => <EditableFormItem />
  const { refetch } = useQuery({
    queryKey: [
      mode,
      props.fields,
      props.orderData,
      props.orderSetting,
      settingAutoGenerateData,
    ],
    queryFn: () => {
      const fieldUpdate = placeHolderField?.field || ''
      const sortField = orderBy(
        props?.orderSetting?.sorted_fields,
        ['display_order'],
        ['asc']
      )
      const keyDefaultValue: any = keyBy(defaultFieldValue, 'field')
      const getKeySortField = map(sortField, 'field')

      const actionFieldValue = getValueDynamic(
        props.orderData,
        actionField?.data_source
      )
      const fields = props?.fields || []

      let coverFields = orderBy(
        fields,
        (item) =>
          item.display_order ??
          keyDefaultValue[item.field]?.display_order ?? ['asc']
      )

      if (size(getKeySortField)) {
        coverFields = sortBy(props.fields, [
          (item) => {
            const index = getKeySortField.indexOf(item.field)
            return index === -1 ? Infinity : index
          },
          (item) => item.display_order ?? 0,
        ])
      }

      if (mode === 'create') {
        handleRenderSetFormItem(coverFields)
        return coverFields
      }

      if (mode === 'clone') {
        const valueObj = { [`${actionField.field}`]: actionFieldValue }
        const testv = handleGenerateValueByKey(valueObj)
        props.form.setFieldsValue({
          [fieldUpdate]: testv.value,
          sequential_number: testv.stringNumber,
        })

        handleRenderSetFormItem(coverFields)

        return coverFields
      }
      if (mode === 'edit') {
        handleRenderSetFormItem(coverFields)
        return coverFields
      }
    },
  })

  const handleAddProperty = () => {
    setErrorExistField(false)
    setOpenAddProperty(true)
  }

  const handleCloseAddProperty = () => {
    setStateSelectProperty(null)
    setOpenAddProperty(false)
  }

  const onEditProperty = (value) => {
    setStateSelectProperty(value)
    handleAddProperty()
  }
  const handleGenerateValueByKey = (
    changedValues: Record<string, unknown> = {}
  ) => {
    const getExistingActionField = getValueDynamic(
      props.orderData,
      actionField?.data_source
    )

    const getKey = keys(changedValues)[0]
    const getValue = changedValues[getKey]
    const isMatchActionKey =
      settingAutoGenerateData?.action_field?.field === getKey

    if (isMatchActionKey) {
      const fieldUpdate =
        settingAutoGenerateData?.place_holder_field?.field || ''
      const holderPrefix =
        settingAutoGenerateData?.place_holder_field?.prefix || ''

      if (fieldUpdate) {
        const [sourceTriggerData, sourceSequentialData, yearKey] =
          itemAutoGenerate

        if (mode === 'edit') {
          if (getValue === getExistingActionField) {
            const existingSequential = getValueDynamic(
              props.orderData,
              sourceSequentialData?.data_source
            )
            const getExistingPlaceHolder = getValueDynamic(
              props.orderData,
              placeHolderField?.data_source
            )

            return {
              value: getExistingPlaceHolder,
              stringNumber: existingSequential?.value_format,
            }
          }
        }

        const { year: getYear } = getDateObject(yearKey?.data_source)

        const sequenceNumber = find(
          sequentialItems,
          (item) =>
            item.entity_type === getKey && item.entity_value === getValue
        )

        const [, stringNumber] = padNumber(
          sequenceNumber?.last_number,
          sourceSequentialData?.padding
        )
        const value = getValue
          ? `${holderPrefix}${getValue}${sourceTriggerData.suffix}${stringNumber}${yearKey.prefix}${getYear}`
          : holderPrefix

        return { value, stringNumber }
      }
    }
    return { value: '', stringNumber: '' }
  }

  const handleAutoGenerateFormItem = (
    changedValues: Record<string, unknown>
  ) => {
    const fieldUpdate = settingAutoGenerateData?.place_holder_field?.field || ''
    const { value, stringNumber } = handleGenerateValueByKey(changedValues)

    if (value) {
      props.form?.setFieldValue(fieldUpdate, value)
    }

    if (stringNumber) {
      props.form?.setFieldValue('sequential_number', stringNumber)
    }
  }

  // Form Item change will trigger this function.
  const onOrderDetailFormChange = (changedValues: Record<string, unknown>) => {
    handleAutoGenerateFormItem(changedValues)
    setAtomFieldChange(true)
  }

  const handleRemoveProperty = (value: IInputProperty) => {
    props.form.setFieldValue(value.field, null)
    const tmpData = props.fields.filter((item) => item.field != value.field)
    props.addOnProperties(tmpData)
    handleCloseAddProperty()
  }

  const doesFieldExist = (objField: any) => {
    let result = false

    if (props.fields?.some((item) => item.field === objField?.field)) {
      result = true
    }
    return result
  }

  const doesUpdateFieldExist = (objField: any) => {
    let result = false
    const getRequire = objField.required
    const findField = find(
      props.fields,
      (item) => item.field === objField?.field
    )

    if (doesFieldExist(objField) && findField.required && getRequire) {
      result = true
    }

    return result
  }

  const handleUpdateProperty = async (value: any) => {
    const newField = {
      ...stateSelectProperty,
      field: camelCase(value.label),
      name: value.label,
      required: value.required,
    }

    if (doesUpdateFieldExist(newField)) {
      setErrorExistField(true)
      refetch() // Trick to re-render the form
    } else {
      if (newField.data_type == 'select') {
        await verifyPropertyExist(newField, user)
      }

      const result = map(props.fields, (item) =>
        item.field === stateSelectProperty.field ? newField : item
      )

      props.addOnProperties(result)
      handleCloseAddProperty()
    }
  }

  const handleCreateProperty = async (value: any) => {
    setLoadingAddPropertyValue(true)
    let fieldValue: any = ''
    if (includes(['image', 'file'], value.dataType)) {
      fieldValue = []
    }
    const getLastDisplayOrder = lastDisplayOrder(stateAllProperties)

    const newField = {
      field: camelCase(value.label),
      name: value.label,
      value: fieldValue,
      data_type: value.dataType,
      required: value.required,
      display_order: getLastDisplayOrder,
    }

    if (doesFieldExist(newField)) {
      setErrorExistField(true)
      refetch() // Trick to re-render the form
      setLoadingAddPropertyValue(false)
    } else {
      setOpenAddProperty(false)
      if (newField.data_type == 'select') {
        await verifyPropertyExist(newField, user)
      }

      const concatNewField = [...stateAllProperties, newField]

      props.addOnProperties(concatNewField)
      setLoadingAddPropertyValue(false)
      setStateSelectProperty(null)
    }
  }

  useEffect(() => {
    const getFormItem = props.form.getFieldValue('buyer')
    if (getFormItem) {
      onOrderDetailFormChange({
        buyer: getFormItem,
      })
    }
  }, [sequentialItems, sequentialItemsRefetch])

  const handleRenderEditForm = () => {
    const value = hideProperties?.value || []
    return map(stateAllProperties, (item, index) => {
      const getFromForm = props.form.getFieldValue(item.field)
      item.value = getFromForm

      return (
        <EditableFormItem
          key={item.field || index}
          item={item}
          orderSetting={props.orderSetting} // The place to render item by company setting
          type={item.data_type}
          form={props.form}
          onEdit={onEditProperty}
          autoPreGenerateField={settingAutoGenerateData?.place_holder_field}
          hideProperties={value}
          disabled={
            typeKey !== 'clone' &&
            disableFields(
              props.orderSetting?.internal_fields,
              item.field,
              currentUserAssignment(props.orderData?.assignments, user)
            )
          }
        />
      )
    })
  }

  if (IsLoadinSupplier) return <LoadingComponent />

  return (
    <Fade>
      <div className="pbf-order-detail-form">
        {loadingAddPropertyValue && <LoadingComponent />}
        <Form
          requiredMark={true}
          form={props.form}
          onValuesChange={onOrderDetailFormChange}
          className="row-container"
        >
          <RequiredFields
            form={props.form}
            orderData={props.orderData}
            orderSetting={props.orderSetting}
            orderAssignments={props.orderData?.assignments}
            supplier={supplier}
          />
          {handleRenderEditForm()}
        </Form>
        {!IsExternalUser(props.orderData, user) && (
          <Button className="add-property-button" onClick={handleAddProperty}>
            <PlusOutlined />
            Add a Property
          </Button>
        )}
        {isEmptyValue(props.orderData) && (
          <Button className="add-property-button" onClick={handleAddProperty}>
            <PlusOutlined />
            Add a Property
          </Button>
        )}
      </div>
      {openAddProperty && (
        <AddProperty
          open={openAddProperty}
          item={stateSelectProperty}
          onRemove={handleRemoveProperty}
          onClose={handleCloseAddProperty}
          onCreate={handleCreateProperty}
          onUpdate={handleUpdateProperty}
          errorExistField={errorExistField}
        />
      )}
    </Fade>
  )
}

export default OrderDetailForm
